import _ from "underscore"
import $ from "jquery"
import TokensHighlighter from "../../../util/TokensHighlighter"
import WindowSizeDependedView from "../../../WindowSizeDependedView"
import SortableTableHeader from "../../../controls/SortableTableHeader"
import StringTokenizer from "../../../util/StringTokenizer"
import ScrollView from "../../../ScrollView"
import AllCustomersNode from "./AllCustomersNode"
import CustomerNode from "./CustomerNode"
import ProjectNode from "./ProjectNode"
import OtherProjectsNode from './OtherProjectsNode'
import {buildVisibleCustomers, buildVisibleCustomersFiltered, getVisibleCustomerIds, getVisibleProjectIds} from './utils'
import {getDiffOfSets} from '../../../atUtils'

export default class Tree extends WindowSizeDependedView{
    constructor({
        onSingleCPSelectionChanged = () => {},
        addCollapsedCustomers = () => {},
        removeCollapsedCustomers = () => {},
        onIsCustomersAscChanged = () => {},
        onVisibleCustomerIdsChanged = () => {},
        onVisibleProjectIdsChanged = () => {},
    } = {}) {
        super();

        this.actions = {
            onSingleCPSelectionChanged,
            addCollapsedCustomers,
            removeCollapsedCustomers,
            onIsCustomersAscChanged,
            onVisibleCustomerIdsChanged,
            onVisibleProjectIdsChanged,
        }

        this.state = {
            singleCustomerProjectSelection: {},
            filterCustomerProjectSelection: {},
            customers: {},
            projects: {},
            nameFilter: '',
            editableCustomerIds: {},
            editableProjectIds: {},
            editingCustomerId: null,
            editingProjectId: null,
            recentlyCreatedCustomerIds: [],
            recentlyCreatedProjectIds: [],
            suppressScroll: false,
            collapsedCustomersSet: {},
            cpSelectorOpened: false,
            cpSelectorFiltered: false,
            isCustomersAsc: true,
        }

        this.onStatePropertyChanged('isCustomersAsc', () => {
            this._updateUnfilteredNodes();
            if (this.filtered)
                this._showFiltered();
            else
                this._showUnfiltered();
        })

        this.onStatePropertyChanged('editingCustomerId', this.handleCustomerEditing)
        this.onStatePropertyChanged('editingProjectId', this.handleProjectEditing)

        this.onAnyStatePropertyDeeplyChanged(['editableCustomerIds', 'editableProjectIds'], () => this._updateManageableNodes())

        this.onAnyStatePropertyDeeplyChanged([
            'nameFilter', 'customers', 'projects',
            'filterCustomerProjectSelection', 'recentlyCreatedCustomerIds', 'recentlyCreatedProjectIds',
        ], state => {
            if (state.nameFilter !== '') {
                this._showFiltered()
            } else {
                this._updateUnfilteredNodes()
                this._showUnfiltered()
            }

            this.actions.onVisibleCustomerIdsChanged(getVisibleCustomerIds(this.nodes))
            this.actions.onVisibleProjectIdsChanged(getVisibleProjectIds(this.nodes))
        })

        this.onAnyStatePropertyDeeplyChanged(['customers', 'projects'], () => {
            this._scrollToEditingEntity()
        })

        this.onStatePropertyDeeplyChanged('singleCustomerProjectSelection', () => {
            this._updateNodesWithSpecialStatus();
        })

        this.onStatePropertyDeeplyChanged('recentlyCreatedCustomerIds', recentlyCreatedCustomerIds => {
            if (recentlyCreatedCustomerIds.length > 0) {
                this._currentScrollView().scrollToItem(this.nodes.map['c' + recentlyCreatedCustomerIds[0]])
            }
        })

        this.onStatePropertyDeeplyChanged('recentlyCreatedProjectIds', recentlyCreatedProjectIds => {
            if (recentlyCreatedProjectIds.length > 0) {
                this._currentScrollView().scrollToItem(this.nodes.map['p' + recentlyCreatedProjectIds[0]])
            }
        })

        this.onStatePropertyDeeplyChanged('collapsedCustomersSet', (collapsedSet, prevCollapsedSet) => {
            this._updateCollapsedStateOfNodes(prevCollapsedSet, collapsedSet)
            this._currentScrollView().setItems(this._filterCollapsedNodes(this.nodes.array));
            this.visibleCollapsedCustomersSet = Object.keys(collapsedSet).filter(id => !!this.nodes.map['c' + id])
        })

        this.onStatePropertyChanged('cpSelectorOpened', (cpSelectorOpened) => {
            this.$filterButton.toggleClass('pressed', cpSelectorOpened)
        })

        this.onStatePropertyChanged('cpSelectorFiltered', (cpSelectorFiltered) => {
            this.$filterButton.toggleClass('filtered', cpSelectorFiltered)
        })
    }

    _scrollToEditingEntity() {
        if (this.state.editingCustomerId)
            this._currentScrollView().scrollToItem(this.nodes.map['c' + this.state.editingCustomerId])
        else if (this.state.editingProjectId)
            this._currentScrollView().scrollToItem(this.nodes.map['p' + this.state.editingProjectId])
    }

    handleCustomerEditing = (editingCustomerId, prevEditingCustomerId) => {
        if (prevEditingCustomerId) this._setNodeEditing("c" + prevEditingCustomerId, false)
        if (editingCustomerId) this._setNodeEditing("c" + editingCustomerId, true)
    }

    handleProjectEditing = (editingProjectId, prevEditingProjectId) => {
        if (prevEditingProjectId) this._setNodeEditing("p" + prevEditingProjectId, false)
        if (editingProjectId) this._setNodeEditing("p" + editingProjectId, true)
    }

    templateHtml(){
        return "<div class='customersProjectsTree'>" +
        "  <div class='header'>" +
        "    <div class='collapseAllButton'></div>" +
        "    <div class='separator'>&nbsp;</div>" +
        "    <div class='customerNameHeaderContainer'></div>" +
        "    <div class='separator'>&nbsp;</div>" +
        "    <div class='filterCPSelectorContainer'>" +
        "       <div class='commonVisibleFilterButton greyBackground'>" +
        "           <div class='mainIcon'></div>" +
        "           <div id='customersProjectsPanelFilterCPSelectorContainer' class='buttonContainer label ellipsis'></div>" +
        "           <div class='downIcon'></div>" +
        "       </div>" +
        "    </div>" +
        "  </div>" +
        "  <div class='unfilteredContainer'></div>" +
        "  <div class='filteredContainer'></div>" +
        "  <div class='notFoundMessage'><%-notFoundMessage%></div>" +
        "</div>"
    }

    afterRender(){
        this.find(".collapseAllButton").on("click", () => {
            if (_.isEmpty(this.visibleCollapsedCustomersSet))
                this._collapseAll();
            else
                this._expandAll();
        })

        this.customerNameHeader = new SortableTableHeader(this.find(".customerNameHeaderContainer"), true, this.state.isCustomersAsc, "common.Customer").init();
        this.customerNameHeader.on("click", () => {
            this.actions.onIsCustomersAscChanged(this.customerNameHeader.isAsc())
        });

        this.stringTokenizer = new StringTokenizer(StringTokenizer.PUNCTUATORS_FOR_SEARCH);
        this.$unfilteredContainer = this.find(".unfilteredContainer");
        this.$filteredContainer = this.find(".filteredContainer");

        this.unfilteredScrollView = new ScrollView(this.$unfilteredContainer, 40).init();
        this.filteredScrollView = new ScrollView(this.$filteredContainer, 40).init();

        this._updateUnfilteredNodes();

        WindowSizeDependedView.prototype.afterRender.call(this);

        $(document).on("taskManagement_visibleCustomersWillChange", () =>{
            _.each(this.nodes.array, node => {
                node.disableRendering()
            })
        })

        this.$filterButton = this.find('.filterCPSelectorContainer .commonVisibleFilterButton')
    }

    adjustSize(){
        WindowSizeDependedView.prototype.adjustSize.call(this);
        if (!this.filtered)
        {
            this.unfilteredScrollView.adjustHeightToWindowSize();
            this.unfilteredScrollViewHeightValid = true;
            this.filteredScrollViewHeightValid = false;
        }
        else
        {
            this.filteredScrollView.adjustHeightToWindowSize();
            this.filteredScrollViewHeightValid = true;
            this.unfilteredScrollViewHeightValid = false;
        }
    }

    messageKeys(){
        return {
            notFoundMessage: "task.list.cp_not_found"
        }
    }

    _updateUnfilteredNodes(){
        this.unfilteredScrollViewHeightValid = false;
        const data = buildVisibleCustomers(this.state.filterCustomerProjectSelection, this.state.customers, this.state.projects, this.state.editingCustomerId, this.state.editingProjectId)
        this.visibleCollapsedCustomersSet = data.filter(c => this.state.collapsedCustomersSet[c.customerId])

        this.unfilteredNodes = this._buildNodes(this.unfilteredScrollView, data, this.state.collapsedCustomersSet, null);
        this.unfilteredScrollView.setItems(this._filterCollapsedNodes(this.unfilteredNodes.array));
        if (!this.filtered)
        {
            this.nodes = this.unfilteredNodes;
            this._updateNodesWithSpecialStatus();
            this._updateAllExpandedState();
            this._updateNotFoundState();
            this.unfilteredScrollView.adjustHeightToWindowSize();
            this.unfilteredScrollViewHeightValid = true;
        }
    }

    _showFiltered(){
        this.filtered = true;
        this.$el.addClass("filtered");

        if (!this.filteredScrollViewHeightValid)
        {
            this.filteredScrollView.adjustHeightToWindowSize();
            this.filteredScrollViewHeightValid = true;
        }

        const text = this.state.nameFilter
        const tokens = this.stringTokenizer.getTokens(text)

        const data = buildVisibleCustomersFiltered(this.state.filterCustomerProjectSelection, this.state.customers, this.state.projects,
            this.state.editingCustomerId, this.state.editingProjectId,
            this.state.recentlyCreatedCustomerIds, this.state.recentlyCreatedProjectIds, text)
        this.visibleCollapsedCustomersSet = data.filter(c => this.state.collapsedCustomersSet[c.customerId])

        this.nodes = this._buildNodes(this.filteredScrollView,
            data,
            this.state.collapsedCustomersSet,
            text && tokens.length > 0 ? new TokensHighlighter(tokens, this.stringTokenizer.getPunctuators()) : null)
        this.filteredScrollView.setItems(this._filterCollapsedNodes(this.nodes.array))
        this._updateNodesWithSpecialStatus()
        this._updateAllExpandedState()
        this._updateNotFoundState()
    }

    _showUnfiltered(){
        this.filtered = false;
        this.$el.removeClass("filtered");

        this.nodes = this.unfilteredNodes;

        this._updateNodesWithSpecialStatus();
        this._updateAllExpandedState();
        this._updateNotFoundState();

        if (!this.unfilteredScrollViewHeightValid)
        {
            this.unfilteredScrollView.adjustHeightToWindowSize();
            this.unfilteredScrollViewHeightValid = true;
        }
    }

    _updateNodesWithSpecialStatus(){
        let findAndSelectNode = (nodeId) => {
            let node = this.nodes.map[nodeId]
            this._setSelectedNode(node)
        }

        const singleCPSelection = this.state.singleCustomerProjectSelection;
        if (singleCPSelection.projectId)
            findAndSelectNode("p" + singleCPSelection.projectId);
        else if (singleCPSelection.customerId)
            findAndSelectNode("c" + singleCPSelection.customerId);
        else {
            findAndSelectNode("all");
            this._currentScrollView().scrollToItem(this.nodes.map["all"])
        }

        let editingCustomerKey = this.state.editingCustomerId ? "c" + this.state.editingCustomerId : null
        let editingProjectKey = this.state.editingProjectId ? "p" + this.state.editingProjectId : null

        for (let key in this.nodes.map) {
            if (this.nodes.map.hasOwnProperty(key)) {
                this._setNodeEditing(key, key === editingCustomerKey || key === editingProjectKey)
            }
        }
    }

    _buildNodes(scrollView, data, collapsedSet, tokensHighlighter){
        const nodesMap = {}
        const nodesArray = []

        const addNode = (node, id) => {
            nodesArray.push(node);
            nodesMap[id] = node;
            return node;
        }

        const onProjectNodeSelect = (project) => this.actions.onSingleCPSelectionChanged({customerId: project.customerId, projectId: project.id})
        const onCustomerNodeSelect = (customerId) => this.actions.onSingleCPSelectionChanged({customerId, projectId: null})

        const onCustomerNodeCollapse = (e) => {
            if (e.target.isCollapsed())
                this.actions.addCollapsedCustomers([e.target.customer.id])
            else
                this.actions.removeCollapsedCustomers([e.target.customer.id])

            this._updateAllExpandedState();
        }

        if (!this.state.nameFilter) {
            const onAllCustomersNodeSelect = () => this.actions.onSingleCPSelectionChanged({customerId: null, projectId: null})

            addNode(new AllCustomersNode({
                onClick: onAllCustomersNodeSelect
            }), "all")
        }

        const visibleCustomers = _.clone(data)

        if (!this.state.isCustomersAsc)
            visibleCustomers.reverse()

        visibleCustomers.forEach(visibleCustomer => {
            const collapsed = !!collapsedSet[visibleCustomer.customerId];
            const customerNode = addNode(new CustomerNode({
                tokensHighlighter,
                customer: this.state.customers[visibleCustomer.customerId],
                collapsed,
                onClick: () => onCustomerNodeSelect(visibleCustomer.customerId)
            }), "c" + visibleCustomer.customerId)
            customerNode.setState({editable: !!this.state.editableCustomerIds[visibleCustomer.customerId]})
            customerNode.on("collapse", onCustomerNodeCollapse);

            visibleCustomer.projectIds.forEach(projectId => {
                const projectNode = addNode(new ProjectNode({
                    tokensHighlighter,
                    project: this.state.projects[projectId],
                    onClick: () => onProjectNodeSelect(this.state.projects[projectId])
                }), "p" + projectId)
                projectNode.setState({editable: !!this.state.editableProjectIds[projectId]})
            })

            if (tokensHighlighter !== null && visibleCustomer.otherProjectIds.length > 0) {
                const otherProjectNode = addNode(new OtherProjectsNode({
                    onClick: () => {
                        scrollView.setItems(this._filterCollapsedNodes(nodesArray))
                        this.actions.onVisibleProjectIdsChanged(getVisibleProjectIds(this.nodes))
                    }
                }), "other" + visibleCustomer.customerId)
                otherProjectNode.setState({onlyOtherProjectsInCustomer: visibleCustomer.projectIds.length === 0})

                visibleCustomer.otherProjectIds.forEach(projectId => {
                    const projectNode = addNode(new ProjectNode({
                        tokensHighlighter: null,
                        project: this.state.projects[projectId],
                        onClick: () => onProjectNodeSelect(this.state.projects[projectId])
                    }), "p" + projectId);
                    projectNode.setState({editable: !!this.state.editableProjectIds[projectId]})
                })
            }
        })

        return {
            map: nodesMap,
            array: nodesArray
        }
    }

    _setSelectedNode(node){
        if (this.selectedNode === node)
            return;

        if (this.selectedNode)
            this.selectedNode.setState({selected: false})

        if (node) node.setState({selected: true})

        this.selectedNode = node
    }

    _setNodeEditing(nodeKey, editing){
        var node = this.nodes.map[nodeKey];
        if (node) node.setState({editing});
    }

    _filterCollapsedNodes(nodes) {
        const result = [];
        let hasCollapsedNodeAbove = false;
        nodes.forEach(node => {
            const isCustomerNode = node instanceof CustomerNode;
            const isOtherProjectNode = node instanceof OtherProjectsNode;

            if (hasCollapsedNodeAbove) {
                if (isCustomerNode)
                    hasCollapsedNodeAbove = false;
                else
                    return;
            }

            if ((isCustomerNode || isOtherProjectNode) && node.isCollapsed())
                hasCollapsedNodeAbove = true;

            result.push(node);
        })

        return result;
    }

    _collapseAll(){
        const collapsedIds = []
        _.each(this.nodes.array, node => {
            if (node instanceof CustomerNode && !node.isCollapsed())
            {
                collapsedIds.push(node.getId())
            }
        });
        this.actions.addCollapsedCustomers(collapsedIds)

        this._updateAllExpandedState();
    }

    _expandAll(){
        const expandedIds = []
        _.each(this.nodes.array, node => {
            if (node instanceof CustomerNode && node.isCollapsed())
            {
                expandedIds.push(node.getId())
            }
        });
        this.actions.removeCollapsedCustomers(expandedIds)

        this._updateAllExpandedState();
    }

    _updateAllExpandedState(){
        this.$el.toggleClass("allExpanded", _.isEmpty(this.visibleCollapsedCustomersSet));
    }

    _updateNotFoundState(){
        this.$el.toggleClass("notFound", this.nodes.array.length === 0);
    }

    _updateManageableNodes(){
        this.nodes.array.forEach(node => {
            if (node instanceof CustomerNode){
                node.setState({editable: !!this.state.editableCustomerIds[node.getId()]})
            }
            if (node instanceof ProjectNode){
                node.setState({editable: !!this.state.editableProjectIds[node.getId()]})
            }
        })
    }

    _updateCollapsedStateOfNodes(prevCollapsedSet, collapsedSet) {
        const {
            added,
            removed
        } = getDiffOfSets(prevCollapsedSet, collapsedSet)

        Object.keys(added).forEach(cId => {
            if (this.nodes.map['c' + cId])
                this.nodes.map['c' + cId].setCollapsed(true)
        })

        Object.keys(removed).forEach(cId => {
            if (this.nodes.map['c' + cId])
                this.nodes.map['c' + cId].setCollapsed(false)
        })
    }

    /**
     * @returns {ScrollView}
     * @private
     */
    _currentScrollView(){
        return this.filtered ? this.filteredScrollView : this.unfilteredScrollView;
    }
}



// WEBPACK FOOTER //
// ./web/js/taskManagement/customersProjectsPanel/tree/Tree.js